import { msg } from "@lingui/core/macro";
import { getI18n } from "~/lib/i18n/i18n";
import { QC_PASSWORD_CONFIG } from "../password-config";

interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * QC password rules: either 10+ chars (any complexity), or 7+ chars with
 * at least 2 of: uppercase letter, number, special character.
 */
export function validatePassword(password: string): PasswordValidationResult {
  const { longMinLength, shortMinLength, shortRequiredCategories } = QC_PASSWORD_CONFIG;

  if (password.length >= longMinLength) {
    return { isValid: true, errors: [] };
  }

  if (password.length >= shortMinLength) {
    let categories = 0;
    if (/[A-Z]/.test(password)) categories++;
    if (/\d/.test(password)) categories++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) categories++;

    if (categories >= shortRequiredCategories) {
      return { isValid: true, errors: [] };
    }
  }

  return {
    isValid: false,
    errors: [
      getI18n()._(
        msg`Password must be ${longMinLength}+ characters, or ${shortMinLength}+ characters with at least ${shortRequiredCategories} of: uppercase letter, number, special character`,
      ),
    ],
  };
}

/**
 * Generates a password strength score (0-5)
 */
export function getPasswordStrength(password: string): number {
  let strength = 0;

  // Length bonuses
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (password.length >= 16) strength++;

  // Complexity bonuses
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

  // Cap at 5
  return Math.min(strength, 5);
}

/**
 * Get a human-readable password strength label
 */
export function getPasswordStrengthLabel(strength: number): string {
  switch (strength) {
    case 0:
    case 1:
      return getI18n()._(msg`Very Weak`);
    case 2:
      return getI18n()._(msg`Weak`);
    case 3:
      return getI18n()._(msg`Fair`);
    case 4:
      return getI18n()._(msg`Strong`);
    case 5:
      return getI18n()._(msg`Very Strong`);
    default:
      return getI18n()._(msg`Unknown`);
  }
}
